import {
  MenuItemAvailabilityStatus,
  MenuItemAvailabilityStatusWithScheduledChanges,
  MenuItemUpdateData,
} from '@provider-portal/common/api/types';
import { FormItem } from 'widgets/MenuEditor/hooks/useInitialValues';

import { getLocalisedStringValueOrEmpty } from 'utils/locales';

import { Dish, MenuScheme, Option } from './scheme';
import { UIMenuItemAvailabilityStatus, UIMenuItemType } from './types';

// transforms server availability object to UI availability string
export const toUIAvailabilityStatus = (
  availability: MenuItemAvailabilityStatusWithScheduledChanges,
): UIMenuItemAvailabilityStatus => {
  if (availability.status === MenuItemAvailabilityStatus.IN_STOCK) {
    return UIMenuItemAvailabilityStatus.available;
  }

  if (availability.status === MenuItemAvailabilityStatus.OUT_OF_STOCK) {
    return availability.scheduled_status_changes.length
      ? UIMenuItemAvailabilityStatus.notToday
      : UIMenuItemAvailabilityStatus.nevermore;
  }

  if (availability.status === MenuItemAvailabilityStatus.HIDDEN) {
    return UIMenuItemAvailabilityStatus.hidden;
  }

  return UIMenuItemAvailabilityStatus.nevermore;
};

interface ServerAvailabilityRequest {
  to_status:
    | MenuItemAvailabilityStatus.IN_STOCK
    | MenuItemAvailabilityStatus.OUT_OF_STOCK
    | MenuItemAvailabilityStatus.HIDDEN;
  /**
   * This is to support out of stock until tomorrow/end of business day. Server will compute when the transition will
   * happen and schedule an event.
   */
  should_schedule_in_stock_transition?: boolean;
}

// transforms ui availability string to server request
export const toServerAvailabilityStatus = (
  status: UIMenuItemAvailabilityStatus,
): ServerAvailabilityRequest => {
  if (status === UIMenuItemAvailabilityStatus.available) {
    return {
      to_status: MenuItemAvailabilityStatus.IN_STOCK,
      should_schedule_in_stock_transition: false,
    };
  }

  if (status === UIMenuItemAvailabilityStatus.hidden) {
    return {
      to_status: MenuItemAvailabilityStatus.HIDDEN,
      should_schedule_in_stock_transition: false,
    };
  }

  return {
    to_status: MenuItemAvailabilityStatus.OUT_OF_STOCK,
    should_schedule_in_stock_transition: status === UIMenuItemAvailabilityStatus.notToday,
  };
};

export type MenuItem = Dish | Option;

export const getPathToItem = (item: MenuItem, type: UIMenuItemType, menu: MenuScheme) => {
  // if it is a dish, then just return name of category
  if (type === UIMenuItemType.dish) {
    const category = menu.entities.category[item.parentId];
    return category ? getLocalisedStringValueOrEmpty(category.name) : '';
  }
  // if it is option, then we need to build full path Category -> Dish -> Option group
  const optionGroup = menu.entities.optionGroup[item.parentId];
  const dish = optionGroup && menu.entities.dish[optionGroup.parentId];
  const category = dish && menu.entities.category[dish.parentId];
  if (!optionGroup || !dish || !category) {
    return '';
  }

  const optionGroupName = getLocalisedStringValueOrEmpty(optionGroup.name);
  const dishName = getLocalisedStringValueOrEmpty(dish.name);
  const categoryName = getLocalisedStringValueOrEmpty(category.name);

  return `${categoryName} > ${dishName} > ${optionGroupName}`;
};

export const filterByName = (pattern: string, item: { value: string } | null) => {
  if (!item) {
    return false;
  }
  const searchText = pattern.toLowerCase();
  const formatedValue = item.value.toLowerCase();

  return formatedValue.startsWith(searchText);
};

export const isOutOfStock = (item?: {
  availability: MenuItemAvailabilityStatusWithScheduledChanges;
}) => Boolean(item && item.availability.status === MenuItemAvailabilityStatus.OUT_OF_STOCK);

export const isAvailabilityChange = (
  prevItem?: Dish | Option,
  nextItem?: {
    availability: ServerAvailabilityRequest;
    name: string | null;
    price: string;
    description: string | null;
    image: string | null;
  },
) => {
  if (prevItem?.availability.status !== nextItem?.availability.to_status) {
    return true;
  }

  if (
    !prevItem?.availability.scheduled_status_changes.length &&
    nextItem?.availability.should_schedule_in_stock_transition
  ) {
    // oos indefinitely to oos today
    return true;
  }

  if (
    prevItem?.availability.scheduled_status_changes.length &&
    !nextItem?.availability.should_schedule_in_stock_transition
  ) {
    // oos today to oos indefinitely
    return true;
  }

  return false;
};

export const mapFormValuesToMenuUpdateItems = (
  formValues: { [key: number]: FormItem },
  currentMenuItems: { [key: number]: Dish | Option | undefined },
) => {
  // by default we have 0 items to update
  const menuItems: MenuItemUpdateData[] = [];
  const ids = Object.keys(formValues);

  // we use for cycle for better performance
  for (let i = 0; i < ids.length; i += 1) {
    const id = Number(ids[i]);
    // prepare potential data to update with id
    const updateData: MenuItemUpdateData = { menu_item_id: Number(id) };
    const currentMenuItem = currentMenuItems[id];
    const newMenuItem = {
      ...formValues[id],
      availability: toServerAvailabilityStatus(formValues[id].availability),
    };

    // if price change, add this info to updateData
    if (Number(currentMenuItem?.price?.value) !== Number(newMenuItem.price)) {
      updateData.price = { value: Number(newMenuItem.price) };
    }
    // if availability change, add this info to updateData
    if (isAvailabilityChange(currentMenuItem, newMenuItem)) {
      updateData.item_availability = newMenuItem.availability;
    }
    // if updateData has some new data, we add it to list of items to update
    if (updateData.price || updateData.item_availability) {
      menuItems.push(updateData);
    }
  }

  return menuItems;
};
